/**
 * Redux-driven modal openers for auth letter upload and document viewing on the CPT page.
 * @module authLetterModalHandlers
 */
import moment from "moment";
import { Authorization } from "../../../../../data-model/AuthorizationModel";
import { AuthOnFile } from "../../../../../data-model/medicalPaOrdersModel";
import { DocumentTypesForOrder } from "../../../../../enums/documentTypes";
import { ModalId } from "../../../../../enums/modalId";
import { setOpenedModalId } from "../../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../../redux/store/store";
import { getAllDocsForSingleJCode } from "../../../../../utils/getAllDocsForSingleJCode";
import { getAuthLetterDocUploadStatus } from "../../../../../utils/getDocsFromStatus";

/**
 * Opens the NAR letter upload modal prefilled with auth letter metadata and optional incorrect-doc path.
 *
 * @param dispatch - Redux `AppDispatch` used to set the opened modal id
 * @param authStatus - Current J-code authorization status string
 * @param id - J-code row identifier passed through modal metadata
 * @param incorrectLetterDocPath - Optional blob path when re-uploading an incorrect letter
 */
export const handleAuthLetterUpload = (
  dispatch: AppDispatch,
  authStatus: string,
  id: string,
  incorrectLetterDocPath?: string,
  authorizationData?: Authorization,
  isLoading?: boolean,
) => {
  const documentsList = authorizationData?.entries?.map((auth) => {
    const description =
      moment(auth.date).format("MM/DD/YYYY") + " - " + (auth.description ?? "");
    const value = auth.blob_path ?? "";
    return {
      value,
      label: description,
    };
  });

  dispatch(
    setOpenedModalId({
      id: ModalId.NAR_LETTER_MODAL,
      metaData: {
        authStatus: authStatus,
        docType: DocumentTypesForOrder.AUTH_LETTER,
        id: id,
        documentsList: documentsList ?? [],
        documentsLoading: isLoading,
        label: "Auth Letter",
        ...(incorrectLetterDocPath && { incorrectLetterDocPath }),
      },
    }),
  );
};

/**
 * Opens the document viewer modal for an already-uploaded auth letter.
 *
 * Retrieves all documents for the given J-Code, extracts the auth letter
 * upload status, and dispatches the DOC_VIEWER_MODAL_WITH_TITLE.
 *
 * @param dispatch - Redux dispatch function
 * @param jCodeId - The J-Code record identifier
 * @param jCode - The J-Code value
 * @param authStatus - Current authorization status of the J-Code
 * @param authOnFileDetails - Auth-on-file payload used to resolve documents for the J-code
 */
export const handleAuthLetterView = (
  dispatch: AppDispatch,
  jCodeId: string,
  jCode: string,
  authStatus: string,
  authOnFileDetails?: AuthOnFile | null,
) => {
  const allDocs = getAllDocsForSingleJCode(jCodeId, authOnFileDetails ?? {});
  const allUploadedDocs = getAuthLetterDocUploadStatus(
    authStatus,
    allDocs ?? [],
    jCodeId,
    jCode,
  );
  dispatch(
    setOpenedModalId({
      id: ModalId.DOC_VIEWER_MODAL_WITH_TITLE,
      metaData: {
        authStatus: authStatus,
        docData: allUploadedDocs?.[0],
      },
    }),
  );
};
